import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { LogOut } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from '../hooks/use-toast';

import StatsCards from '../components/admin/StatsCards';
import BookingsTab from '../components/admin/BookingsTab';
import WeddingsTab from '../components/admin/WeddingsTab';
import OrdersTab from '../components/admin/OrdersTab';
import SubscribersTab from '../components/admin/SubscribersTab';
import CampaignTab from '../components/admin/CampaignTab';
import ContestTab from '../components/admin/ContestTab';
import ContestArtistsTab from '../components/admin/ContestArtistsTab';
import ContestMarketingTab from '../components/admin/ContestMarketingTab';
import ContestGiftRequestsTab from '../components/admin/ContestGiftRequestsTab';
import CorpTab from '../components/admin/CorpTab';
import PortfolioTab from '../components/admin/PortfolioTab';
import TrackingTab from '../components/admin/TrackingTab';
import ChangePasswordDialog from '../components/admin/ChangePasswordDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SecureAdminDashboard = () => {
  const [bookings, setBookings] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [weddingBookings, setWeddingBookings] = useState([]);
  const [orders, setOrders] = useState([]);
  const [campaignOrders, setCampaignOrders] = useState([]);
  const [campaignStats, setCampaignStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adminInfo, setAdminInfo] = useState(null);
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalBookings: 0,
    totalSubscribers: 0,
    totalWeddings: 0,
    totalOrders: 0,
    pendingBookings: 0,
  });

  // Auth check
  useEffect(() => {
    const checkAuth = async () => {
      const token = sessionStorage.getItem('adminToken');
      if (!token) {
        navigate('/admin/login');
        return;
      }
      try {
        const response = await axios.get(`${API}/admin/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setAdminInfo(response.data.admin);
      } catch {
        sessionStorage.removeItem('adminToken');
        navigate('/admin/login');
      }
    };
    checkAuth();
  }, [navigate]);

  const getAuthHeaders = useCallback(() => {
    const token = sessionStorage.getItem('adminToken');
    return { Authorization: `Bearer ${token}` };
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [bookingsRes, subscribersRes, weddingsRes, ordersRes, campaignRes, campaignStatsRes] = await Promise.all([
        axios.get(`${API}/bookings`, { headers: getAuthHeaders() }),
        axios.get(`${API}/newsletter/subscribers`, { headers: getAuthHeaders() }),
        axios.get(`${API}/weddings`, { headers: getAuthHeaders() }),
        axios.get(`${API}/orders/`, { headers: getAuthHeaders() }),
        axios.get(`${API}/campaign/orders`, { headers: getAuthHeaders() }),
        axios.get(`${API}/campaign/stats`, { headers: getAuthHeaders() }),
      ]);

      if (bookingsRes.data.success) {
        setBookings(bookingsRes.data.bookings);
        setStats((prev) => ({
          ...prev,
          totalBookings: bookingsRes.data.total || bookingsRes.data.bookings.length,
          pendingBookings: bookingsRes.data.bookings.filter((b) => b.status === 'pending').length,
        }));
      }
      if (subscribersRes.data.success) {
        setSubscribers(subscribersRes.data.subscribers);
        setStats((prev) => ({
          ...prev,
          totalSubscribers: subscribersRes.data.total || subscribersRes.data.subscribers.length,
        }));
      }
      if (weddingsRes.data.success) {
        setWeddingBookings(weddingsRes.data.bookings);
        setStats((prev) => ({
          ...prev,
          totalWeddings: weddingsRes.data.total || weddingsRes.data.bookings.length,
        }));
      }
      if (ordersRes.data?.orders) {
        setOrders(ordersRes.data.orders);
        setStats((prev) => ({
          ...prev,
          totalOrders: ordersRes.data.total || ordersRes.data.orders.length,
        }));
      }
      if (campaignRes.data?.orders) setCampaignOrders(campaignRes.data.orders);
      if (campaignStatsRes.data) setCampaignStats(campaignStatsRes.data);
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        sessionStorage.removeItem('adminToken');
        navigate('/admin/login');
      }
      toast({ title: 'Error', description: 'Failed to fetch data', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, navigate]);

  useEffect(() => {
    if (adminInfo) fetchData();
  }, [adminInfo, fetchData]);

  const handleLogout = useCallback(() => {
    sessionStorage.removeItem('adminToken');
    navigate('/admin/login');
    toast({ title: 'Logged Out', description: 'You have been logged out successfully' });
  }, [navigate]);

  const handleStatusChange = useCallback(
    async (orderId, nextStatus) => {
      try {
        const { data } = await axios.patch(
          `${API}/orders/${orderId}/status`,
          { status: nextStatus },
          { headers: getAuthHeaders() }
        );
        if (data.success) {
          toast({ title: 'Status updated', description: `Order is now ${data.status}` });
          await fetchData();
        }
      } catch (error) {
        toast({
          title: 'Update failed',
          description: error.response?.data?.detail || 'Could not update status',
          variant: 'destructive',
        });
      }
    },
    [getAuthHeaders, fetchData]
  );

  const handleSendPaymentLink = useCallback(
    async (orderId) => {
      try {
        const { data } = await axios.post(
          `${API}/orders/${orderId}/payment-link`,
          {},
          { headers: { ...getAuthHeaders(), Origin: window.location.origin } }
        );
        if (data.success) {
          toast({
            title: 'Payment link sent!',
            description: 'Stripe Checkout URL emailed to the customer.',
          });
          await fetchData();
        }
      } catch (error) {
        toast({
          title: 'Could not create payment link',
          description: error.response?.data?.detail || 'Try again or check Stripe config.',
          variant: 'destructive',
        });
      }
    },
    [getAuthHeaders, fetchData]
  );

  const handleCampaignStatusChange = useCallback(
    async (orderId, nextStatus) => {
      try {
        const { data } = await axios.patch(
          `${API}/campaign/orders/${orderId}/status`,
          { status: nextStatus },
          { headers: getAuthHeaders() }
        );
        if (data.success) {
          toast({ title: 'Status updated', description: `Lead is now ${data.status}` });
          await fetchData();
        }
      } catch (error) {
        toast({
          title: 'Update failed',
          description: error.response?.data?.detail || 'Could not update status',
          variant: 'destructive',
        });
      }
    },
    [getAuthHeaders, fetchData]
  );

  const handleApproveCredit = useCallback(
    async (orderId) => {
      try {
        const { data } = await axios.post(
          `${API}/campaign/orders/${orderId}/approve-credit`,
          {},
          { headers: getAuthHeaders() }
        );
        if (data.success) {
          toast({
            title: 'Credit approved!',
            description: `$${data.creditAmount || 25} credit issued to referrer.`,
          });
          await fetchData();
        }
      } catch (error) {
        toast({
          title: 'Approval failed',
          description: error.response?.data?.detail || 'Could not approve credit',
          variant: 'destructive',
        });
      }
    },
    [getAuthHeaders, fetchData]
  );

  if (!adminInfo) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FDB913] mx-auto" />
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Admin Dashboard</h1>
            <p className="text-gray-600">Welcome back, {adminInfo.fullName}</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <ChangePasswordDialog />
            <Button onClick={handleLogout} variant="outline" data-testid="admin-logout-btn">
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>

        <StatsCards stats={stats} />

        <Tabs defaultValue="bookings" className="space-y-4">
          <TabsList className="flex w-full md:w-auto overflow-x-auto flex-nowrap justify-start md:grid md:grid-cols-12 h-auto [&>*]:shrink-0 [&>*]:whitespace-nowrap">
            <TabsTrigger value="bookings" data-testid="tab-bookings">
              Consultations ({bookings.length})
            </TabsTrigger>
            <TabsTrigger value="weddings" data-testid="tab-weddings">
              Weddings ({weddingBookings.length})
            </TabsTrigger>
            <TabsTrigger value="orders" data-testid="tab-orders">
              Orders ({orders.length})
            </TabsTrigger>
            <TabsTrigger value="campaign" data-testid="tab-campaign">
              🍷 Custom Wine ({campaignOrders.length})
            </TabsTrigger>
            <TabsTrigger value="corp" data-testid="tab-corp">
              🏢 Corp Inquiries
            </TabsTrigger>
            <TabsTrigger value="portfolio" data-testid="tab-portfolio">
              🍾 Showcase
            </TabsTrigger>
            <TabsTrigger value="tracking" data-testid="tab-tracking">
              📈 Tracking
            </TabsTrigger>
            <TabsTrigger value="contest" data-testid="tab-contest">
              🎨 WOTY
            </TabsTrigger>
            <TabsTrigger value="artists" data-testid="tab-artists">
              🖼️ Artists
            </TabsTrigger>
            <TabsTrigger value="marketing" data-testid="tab-marketing">
              🍷 Marketing
            </TabsTrigger>
            <TabsTrigger value="gifts" data-testid="tab-gifts">
              🎁 Gifts
            </TabsTrigger>
            <TabsTrigger value="subscribers" data-testid="tab-subscribers">
              Subscribers ({subscribers.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="bookings" className="space-y-4">
            <BookingsTab bookings={bookings} loading={loading} onRefresh={fetchData} />
          </TabsContent>
          <TabsContent value="weddings" className="space-y-4">
            <WeddingsTab weddingBookings={weddingBookings} loading={loading} onRefresh={fetchData} />
          </TabsContent>
          <TabsContent value="orders" className="space-y-4">
            <OrdersTab
              orders={orders}
              loading={loading}
              onRefresh={fetchData}
              onStatusChange={handleStatusChange}
              onSendPaymentLink={handleSendPaymentLink}
            />
          </TabsContent>
          <TabsContent value="campaign" className="space-y-4">
            <CampaignTab
              campaignOrders={campaignOrders}
              campaignStats={campaignStats}
              loading={loading}
              onRefresh={fetchData}
              onStatusChange={handleCampaignStatusChange}
              onApproveCredit={handleApproveCredit}
            />
          </TabsContent>
          <TabsContent value="portfolio" className="space-y-4">
            <PortfolioTab />
          </TabsContent>
          <TabsContent value="tracking" className="space-y-4">
            <TrackingTab />
          </TabsContent>
          <TabsContent value="contest" className="space-y-4">
            <ContestTab />
          </TabsContent>
          <TabsContent value="artists" className="space-y-4">
            <ContestArtistsTab />
          </TabsContent>
          <TabsContent value="marketing" className="space-y-4">
            <ContestMarketingTab />
          </TabsContent>
          <TabsContent value="gifts" className="space-y-4">
            <ContestGiftRequestsTab />
          </TabsContent>
          <TabsContent value="corp" className="space-y-4">
            <CorpTab />
          </TabsContent>
          <TabsContent value="subscribers" className="space-y-4">
            <SubscribersTab subscribers={subscribers} loading={loading} onRefresh={fetchData} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default SecureAdminDashboard;
